import React, { useEffect, useMemo, useState } from "react";
import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import Skeleton from "react-loading-skeleton";
import { useAppDispatch } from "store/hooks";
import {
  downloadPdfFromApi,
  encode,
  formatCurrency,
  formatRange,
  getPaymentMethod,
  showAmount,
} from "utils/HelperFunctions";
import moment from "moment";
import { ExportIcon } from "icons/payments/ExportIcon";
import QuestionsFooter from "components/auth/QuestionsFooter";
import { updateUserData, useUserData } from "store/slices/authSlice";
import { useNavigate } from "react-router-dom";
import Button from "components/form/Button";
import { invoiceDetailsGet } from "store/slices/subscriptionSlice";
import { paymentReceiptDownloadPdf } from "store/slices/paymentsSlice";
import { usePortalVanityUrl } from "store/slices/dashboardSlice";
import PaymentPreview from "components/payments/PaymentPreview";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { InvoiceData, PaymentHistoryItem, InvoiceItem } from "types/invoice";
import { isCacelledBefore } from "utils/Formater/DateFormat";
import { useTestAttrsFactory, useTid } from "utils/testid";
import { formatDisplayDate } from "utils/DateFormat";

interface InvoicePreviewProps {
  id: string | undefined;
  setId: (v: string) => void;
  setIsPreview: (v: boolean) => void;
  isSlug: boolean;
  slug?: string | undefined;
  isPreview?: boolean;
  showAccountDetails?: boolean;
}

function calculateEndDate(
  startDate: any,
  schedule: any,
  intervalCount: any = 1,
  intervalSelector: any = "month"
) {
  if (!startDate) return null;

  const start = new Date(startDate);
  const end = new Date(start);
  const type = String(schedule || "")
    .toLowerCase()
    .trim();

  switch (type) {
    case "day":
      end.setDate(start.getDate() + 1);
      break;
    case "week":
      end.setDate(start.getDate() + 7);
      break;
    case "month":
      end.setMonth(start.getMonth() + 1);
      break;
    case "quarter":
      end.setMonth(start.getMonth() + 3);
      break;
    case "semiannual":
      end.setMonth(start.getMonth() + 6);
      break;
    case "year":
      end.setFullYear(start.getFullYear() + 1);
      break;
    case "custom":
      // Handle dynamic custom intervals
      const count = parseInt(intervalCount) || 1;
      switch (String(intervalSelector || "").toLowerCase()) {
        case "day":
          end.setDate(start.getDate() + count);
          break;
        case "week":
          end.setDate(start.getDate() + count * 7);
          break;
        case "month":
          end.setMonth(start.getMonth() + count);
          break;
        case "year":
          end.setFullYear(start.getFullYear() + count);
          break;
        default:
          end.setMonth(start.getMonth() + count);
      }
      break;
    default:
      end.setMonth(start.getMonth() + 1);
  }

  // Format as YYYY)-MM-DD
  return end;
}

const InvoicePreview = (props: InvoicePreviewProps) => {
  const vanityUrl = usePortalVanityUrl();
  const {
    id,
    setId,
    setIsPreview,
    isSlug = true,
    slug,
    isPreview = false,
    showAccountDetails = true,
  } = props;
  const [paymentDetails, setPaymentDetails] = useState<PaymentHistoryItem[]>(
    []
  );
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
  const [isPaymentPreview, setIsPaymentPreview] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState("");
  const [isInvoiceDownload, setIsInvoiceDownload] = useState<boolean>(false);
  const [isPaymentPreviewId, setIsPaymentPreviewId] = useState<
    string | undefined
  >(undefined);
  const [amount, setAmount] = useState({
    totalAmount: 0,
    paidAmount: 0,
    remainingAmount: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [paymentLogo, setPaymentLogo] = useState<string | undefined>(logo);
  const [showAll, setShowAll] = useState<boolean>(true);
  const [viewMore, setViewMore] = useState<boolean>(true);
  const navigate = useNavigate();
  const userData = useUserData();
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();

  const onClose = () => {
    setId("");
    setIsPreview(false);
    setPaymentDetails([]);
    setInvoiceData(null);
    setIsLoading(true);
    setShowAll(true);
    setViewMore(true);
  };
  const dispatch = useAppDispatch();

  const getAmount = (invoiceData: InvoiceData) => {
    try {
      const invoiceList = invoiceData?.invoiceItems;
      if (invoiceList.length > 0) {
        let amt = 0;
        invoiceList?.forEach((items: InvoiceItem) => {
          if (items?.status?.toLowerCase() === "paid") {
            amt += Number(items?.amount) * (items?.quantity || 1);
          }
        });
        return {
          totalAmount:
            Number(invoiceData?.total_amount) - Number(invoiceData?.conv_fee),
          paidAmount: amt,
          remainingAmount:
            Number(invoiceData?.total_amount) -
            Number(invoiceData?.conv_fee) -
            amt,
        };
      } else {
        return {
          totalAmount:
            Number(invoiceData?.total_amount) - Number(invoiceData?.conv_fee),
          paidAmount: 0,
          remainingAmount: 0,
        };
      }
    } catch (error) {
      console.log("error =====", error);
      return {
        totalAmount:
          Number(invoiceData?.total_amount) - Number(invoiceData?.conv_fee),
        paidAmount: 0,
        remainingAmount: 0,
      };
    }
  };

  const getInvoiceData = async () => {
    setIsLoading(true);
    try {
      let payload: any = { slug };
      let res: any = await dispatch(invoiceDetailsGet(payload)).unwrap();
      if (res?.body?.success) {
        const { invoiceData, companyLogoDetails, customerData } =
          res?.body?.data;
        setInvoiceData(invoiceData as InvoiceData);
        setPaymentDetails(
          (invoiceData?.paymentHistory || []) as PaymentHistoryItem[]
        );
        setAmount(getAmount(invoiceData as InvoiceData));
        setPaymentLogo((companyLogoDetails?.logo as string) || undefined);
        dispatch(
          updateUserData({
            ...userData?.data?.accountData,
            ...customerData,
          })
        );
      }
    } catch (error) {
      console.log("getInvoiceData error =====", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if ((id || slug) && isPreview) {
      getInvoiceData();
    }
  }, [id, slug, isSlug, isPreview]);

  const handleDownloadPDF = async (id: any, receiptId?: any) => {
    let pid = encode(
      `${id}_${invoiceData?.clusterid}_${new Date().getTime()}`,
      "pid_"
    );

    let payload = {
      invoice_id: id as string | number,
      lead_id: invoiceData?.lead_id,
      url: `${vanityUrl}view-payment/${pid}`,
      cluster_id: invoiceData?.clusterid,
      isFromPortal: true,
      // emails: [],
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      `Receipt - ${receiptId || id}.pdf`,
      (loading) => setIsDownloading(loading ? "true" : "")
    );
  };

  const handleDownloadInvoice = async () => {
    let invoice_slug = invoiceData?.url_slug || slug;
    let payload = {
      invoice_slug,
      lead_id: invoiceData?.lead_id,
      cluster_id: invoiceData?.clusterid,
      url: `${vanityUrl}invoice/${invoice_slug}`,
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      `Invoice - ${invoiceData?.invoice_number || invoice_slug}.pdf`,
      setIsInvoiceDownload
    );
  };

  const subsDates = useMemo(() => {
    if (
      !invoiceData?.subscriptionDetails ||
      Object.keys(invoiceData?.subscriptionDetails)?.length === 0
    )
      return null;

    const item = invoiceData?.invoiceItems?.find((item) =>
      /<div[^>]*>.*?<\/div>/.test(item?.description || "")
    );

    if (item && item?.description) {
      return formatRange(item?.description);
    }

    let date1, date2;

    if (invoiceData?.creation_date) {
      date1 = moment(invoiceData?.creation_date).format("MMM D");
    }

    const filteredProds = invoiceData?.invoiceItems?.filter(
      (item) => item?.priceDetails?.frequency === "recurring"
    );

    if (!filteredProds?.length) {
      return null;
    }

    const intervaL_count =
      filteredProds?.[0]?.priceDetails?.interval_custom_count;
    const interval_custom_selector =
      filteredProds?.[0]?.priceDetails?.interval_custom_selector;
    const schedule = filteredProds?.[0]?.priceDetails?.schedule;

    const endDate = calculateEndDate(
      invoiceData?.creation_date,
      schedule,
      intervaL_count,
      interval_custom_selector
    );
    date2 = endDate ? moment(endDate).format("MMM D, YYYY") : null;
    return {
      date1,
      date2,
    };
  }, [invoiceData?.subscriptionDetails]);

  return (
    <>
      <div
        className={`${
          isPaymentPreview ? "bg-bgPreviewSecondary" : "bg-bgSecondary"
        } h-[var(--shell-vh)] overflow-y-auto pt-[70px]`}
        {...testAttrs("invoice-preview", "Invoice preview")}
      >
        <div
          className={`${
            isPaymentPreview ? "bg-bgPreviewHeaderSecondary" : "bg-bgPrimary"
          } lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-[3]`}
          {...testAttrs("invoice-preview-header")}
        >
          <div className="flex items-center gap-2">
            <EyeIcon
              aria-hidden="true"
              strokeColor="var(--color-txt-primary)"
              width="24"
              height="24"
            />
            <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
              Preview
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div
              className="flex items-center gap-2 cursor-pointer"
              role="button"
              tabIndex={0}
              {...testAttrs("download-invoice", "Download invoice")}
              onClick={() => {
                handleDownloadInvoice();
              }}
            >
              {isInvoiceDownload ? (
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              ) : (
                <DownloadIcon
                  aria-hidden="true"
                  strokeColor="var(--color-btn-primary-active)"
                  width="16"
                  height="16"
                />
              )}
              <span className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline">
                {isInvoiceDownload ? "Downloading..." : "Download"}
              </span>
            </div>
            <div
              role="button"
              tabIndex={0}
              {...testAttrs("close-invoice-preview", "Close invoice preview")}
              onClick={() => onClose()}
              className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
            >
              <CloseIcon aria-hidden="true" />
            </div>
          </div>
        </div>
        {isLoading && (
          <div className="lg:px-10 px-5 py-4  lg:text-base text-sm bg-white">
            Invoice <Skeleton width={150} />
          </div>
        )}
        {!isLoading &&
          (invoiceData?.status?.toLowerCase() === "canceled" ||
            invoiceData?.status?.toLowerCase() === "completed" ||
            invoiceData?.status?.toLowerCase() === "paid" ||
            invoiceData?.status?.toLowerCase() === "overdue") && (
            <div
              className={`lg:px-10 px-5 py-4 lg:text-base text-sm text-txtPrimary ${
                invoiceData?.status?.toLowerCase() === "canceled"
                  ? "bg-lightRed2"
                  : invoiceData?.status?.toLowerCase() === "overdue"
                    ? "bg-[#FFEFE6]"
                    : "bg-lightGreen"
              }`}
            >
              Invoice{" "}
              {invoiceData?.status?.toLowerCase() === "canceled"
                ? "canceled"
                : invoiceData?.status?.toLowerCase() === "overdue"
                  ? "was past due"
                  : "paid"}{" "}
              on{" "}
              <span
                className={`${
                  invoiceData.status?.toLowerCase() === "canceled"
                    ? "text-red"
                    : invoiceData?.status?.toLowerCase() === "overdue"
                      ? "text-orange"
                      : "text-green"
                }`}
              >
                {moment(
                  invoiceData?.status?.toLowerCase() === "canceled"
                    ? invoiceData?.canceled_date
                    : invoiceData?.status?.toLowerCase() === "overdue"
                      ? invoiceData?.due_date
                      : invoiceData?.completed ||
                        invoiceData?.invoiceItems[0]?.updated
                ).format("MMMM D, YYYY")}
              </span>
            </div>
          )}
        <div className="py-[16px] lg:px-10 px-5 flex items-center justify-between">
          <div className="lg:text-xl text-lg font-normal leading-normal text-txtPrimary">
            {showAccountDetails ? "Invoice" : "Invoice and payment details"}
          </div>
          <div className="flex items-center justify-end gap-[10px] ">
            {isLoading ? (
              <Skeleton width={100} height={35} />
            ) : paymentLogo ? (
              <div className="h-16 lg:h-16 flex justify-end">
                <img
                  src={paymentLogo}
                  alt="Company logo"
                  className="animate__animated animate__fadeIn object-contain"
                />
              </div>
            ) : (
              <img
                src={logo}
                alt="Pay portal logo"
                className="animate__animated animate__fadeIn"
              />
            )}
            {!paymentLogo && (
              <div className="text-purple font-medium text-xl leading-[30px]">
                CompanyName
              </div>
            )}
          </div>
        </div>
        {showAccountDetails ? (
          <div className="w-full">
            <div className="w-full py-2">
              <div className="flex justify-between pb-2 lg:px-10 px-5 items-center">
                <p className="text-btnPrimaryActive text-xl font-normal">
                  {isLoading ? (
                    <Skeleton width={150} />
                  ) : (
                    `${
                      userData?.data?.accountData?.first_name +
                        " " +
                        userData?.data?.accountData?.last_name || ""
                    }`
                  )}
                </p>
                <p className="flex text-sm text-txtPrimary">
                  {" "}
                  {isLoading ? (
                    <Skeleton width={70} />
                  ) : (
                    `${userData?.data?.accountData.accountid}`
                  )}
                </p>
              </div>
              <div className=" lg:px-10 px-5 flex justify-between gap-1">
                <div className="w-full flex text-sm gap-1 break-words text-txtSecondary">
                  Balance
                  <p className="text-txtPrimary">
                    {isLoading ? (
                      <Skeleton width={70} />
                    ) : (
                      formatCurrency(userData?.data?.accountData?.balance) ||
                      "$0.00"
                    )}
                  </p>
                </div>
                <div className="w-full flex text-sm gap-1 justify-end text-txtSecondary">
                  <p className="w-full break-words text-end">
                    Description{" "}
                    <span className="text-txtPrimary">
                      {isLoading ? (
                        <Skeleton width={70} />
                      ) : (
                        `${userData?.data?.accountData?.description || "--"}`
                      )}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div className="py-2 lg:px-10 px-5">
              <div className="w-full h-[1px] bg-strokePrimary "></div>
            </div>
          </div>
        ) : null}
        <div className="py-2 lg:px-10 px-5 flex items-center justify-between flex-col gap-1">
          <div className="flex justify-between items-center w-full">
            <span className="text-xs font-medium uppercase leading-[15.62px] w-full text-txtSecondary">
              Amount
            </span>
            {invoiceData?.status && invoiceData?.status !== "PAID" ? (
              <span className="text-xs font-medium uppercase leading-[15.62px] w-full text-txtSecondary text-right">
                Due Date
              </span>
            ) : null}
          </div>
          <div className="flex justify-between items-center w-full">
            <span className="text-2xl leading-[31.25px] w-full text-txtPrimary">
              {isLoading ? (
                <Skeleton width={100} height={30} />
              ) : (
                // formatCurrency(invoiceData?.total_amount) || "$0.00"
                formatCurrency(amount?.totalAmount) || "$0.00"
              )}
            </span>
            {invoiceData?.status && invoiceData?.status !== "PAID" ? (
              <span className="text-sm leading-[31.25px] w-full text-txtPrimary text-right">
                {formatDisplayDate(invoiceData?.due_date, "MM/DD/YYYY")}
              </span>
            ) : null}
          </div>
        </div>

        <div className="py-2 lg:px-10 px-5 flex items-center justify-between flex-col w-full">
          <div className="border border-strokePrimary rounded-lg py-5 lg:px-6 px-4 bg-bgPrimary w-full flex flex-col gap-5">
            <div className="w-full flex items-center justify-between text-sm text-txtPrimary leading-[18.23px]">
              <span className="font-bold">
                Invoice #
                {isLoading ? (
                  <Skeleton width={70} />
                ) : (
                  <span className="uppercase">
                    {invoiceData?.invoice_number}
                  </span>
                )}
              </span>
              {subsDates && !isLoading && (
                <span>
                  {subsDates?.date1 && <>{subsDates?.date1}</>}{" "}
                  {subsDates?.date2 && <>- {subsDates?.date2}</>}
                </span>
              )}
            </div>
            <div className="w-full h-[1px] bg-strokePrimary" />
            {isLoading &&
              [1, 2]?.map((_, index) => (
                <div className="w-full" key={index}>
                  <div className="flex justify-between items-center text-txtPrimary text-sm font-medium leading-[18.23px]">
                    <span>
                      <Skeleton width={150} />
                    </span>
                    <span>
                      <Skeleton width={70} />
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-txtSecondary text-sm leading-[18.23px]">
                    <span className="flex">
                      Qty <Skeleton width={30} />
                    </span>
                    <span className="flex">
                      <Skeleton width={40} />
                      each
                    </span>
                  </div>
                </div>
              ))}
            {invoiceData &&
              !isLoading &&
              invoiceData?.invoiceItems?.length > 0 &&
              [...(invoiceData?.invoiceItems || [])]
                .sort((a: InvoiceItem, b: InvoiceItem) => {
                  const aw = a?.priceDetails?.frequency === "recurring" ? 1 : 0;
                  const bw = b?.priceDetails?.frequency === "recurring" ? 1 : 0;
                  return bw - aw;
                })
                .map((item: InvoiceItem, index: number) => {
                  const itemKey =
                    item?.description ||
                    item?.productDetails?.name ||
                    `${item?.amount || "amount"}-${item?.quantity || 1}-${index}`;
                  if (showAll && index >= 5) {
                    return null;
                  } else {
                    return (
                      <div
                        className="w-full"
                        key={itemKey}
                        data-testid={tid(`invoice-item-${itemKey}`)}
                      >
                        <div className="flex justify-between items-center text-txtPrimary text-sm font-medium leading-[18.23px]">
                          <span>
                            {item?.productDetails?.name ||
                              `Inovice ${index + 1}`}
                          </span>
                          <span>
                            {formatCurrency(
                              Number(item?.amount) * (item?.quantity || 1)
                            ) || "$0.00"}{" "}
                            {/* {item?.priceDetails?.schedule &&
                              item?.priceDetails?.schedule !== "" &&
                              `/ ${changeScheduleName(item?.priceDetails)}`} */}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-txtSecondary text-sm leading-[18.23px]">
                          <span>Qty {item?.quantity}</span>
                          <span>
                            {formatCurrency(item?.amount) || "$0.00"} each
                          </span>
                        </div>
                      </div>
                    );
                  }
                })}
            {invoiceData?.invoiceList &&
              invoiceData?.invoiceList?.length > 5 &&
              !isLoading && (
                <div
                  className="text-blue hover:!text-btnPrimaryHover text-center w-full cursor-pointer"
                  role="button"
                  tabIndex={0}
                  {...testAttrs("toggle-invoice-items", "Toggle invoice items")}
                  onClick={() => {
                    setShowAll(!showAll);
                  }}
                >
                  Show {showAll ? " all " : " less "} items
                </div>
              )}
            <div className="w-full h-[1px] bg-strokePrimary" />
            <div className="flex w-full justify-between items-center text-sm uppercase text-txtPrimary leading-[18.23px] font-bold">
              <span>Total</span>
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                <span>{showAmount(amount?.totalAmount) || "$0.00"}</span>
              )}
            </div>
            <div className="w-full h-[1px] bg-strokePrimary" />
            <div className="flex flex-col gap-4 w-full justify-between items-center">
              <div className="flex w-full justify-between items-center text-sm uppercase text-txtPrimary leading-[18.23px] font-bold">
                <span>Amount Paid</span>
                {isLoading ? (
                  <Skeleton width={100} />
                ) : (
                  <span>
                    {amount?.paidAmount === 0
                      ? formatCurrency(amount?.paidAmount)
                      : `(${formatCurrency(amount?.paidAmount)})`}
                  </span>
                )}
              </div>
              <div className="flex w-full justify-between items-center text-sm uppercase text-txtPrimary leading-[18.23px] font-bold">
                <span>Amount Remaining</span>
                {isLoading ? (
                  <Skeleton width={100} />
                ) : (
                  <span>{showAmount(amount?.remainingAmount) || "$0.00"}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        {(!showAccountDetails || invoiceData?.status === "PAID") && (
          <div className="py-2 lg:px-10 px-5 flex items-center justify-between flex-col w-full">
            <div className="border border-strokePrimary rounded-lg py-5 lg:px-6 px-4 bg-bgPrimary w-full flex flex-col gap-5">
              <div className="w-full text-sm uppercase text-txtPrimary leading-[18.23px] font-bold">
                Payment History
              </div>
              <div className="w-full h-[1px] bg-strokePrimary" />
              {!isLoading && paymentDetails?.length > 0 ? (
                <div className="w-full flex flex-col gap-5">
                  {paymentDetails?.map((item: any, index: number) => {
                    const paymentKey =
                      item?.id || item?.transaction_id || index;
                    if (viewMore && index >= 3) {
                      return null;
                    }
                    return (
                      <div
                        key={paymentKey}
                        className="w-full flex justify-between items-center text-sm"
                        data-testid={tid(`payment-history-row-${paymentKey}`)}
                      >
                        <span className="w-[23%] text-start text-txtPrimary flex gap-[3px] items-center">
                          {formatDisplayDate(
                            item?.paymentDate || item.insert_date,
                            "MM/DD/YYYY"
                          )}
                          <span
                            className="cursor-pointer"
                            role="button"
                            tabIndex={0}
                            aria-label="Preview payment receipt"
                            data-testid={tid(
                              `preview-payment-receipt-${paymentKey}`
                            )}
                            onClick={() => {
                              setIsPaymentPreview(true);
                              setIsPaymentPreviewId(String(item?.id));
                            }}
                          >
                            <ExportIcon
                              aria-hidden="true"
                              className="group"
                              _className="group-hover:!fill-iconObscure"
                            />
                          </span>
                        </span>
                        <div className="w-[25%] text-start ">
                          {item?.payment_method
                            ? getPaymentMethod({
                                card_type: item?.card_type,
                                method: item?.payment_method,
                                digits: item?.card_last_4_digits,
                                account_number: item?.card_last_4_digits,
                                className: "!gap-[2px]",
                                textClassName: "!text-txtSecondary",
                                rowId: item?.id,
                                isHidden: false,
                              })
                            : "--"}
                        </div>
                        <span className="text-end text-txtPrimary">
                          {formatCurrency(
                            item?.amount + 0
                            // (item?.payment_method === "ACH"
                            //   ? item?.ach_fee_amount
                            //   : 0) +
                            // (item?.payment_method === "CC" ||
                            // item?.payment_method === "CAPP" ||
                            // item?.payment_method === "GP" ||
                            // item?.payment_method === "AP" ||
                            // item?.payment_method === "CC" ||
                            // item?.payment_method === "AMZP"
                            //   ? item?.cc_fee_amount
                            //   : 0),
                          ) || "$0.00"}
                        </span>
                      </div>
                    );
                  })}
                  {paymentDetails?.length > 3 && (
                    <div className="w-full text-blue hover:!text-btnPrimaryHover text-sm ">
                      <span
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        {...testAttrs(
                          "toggle-payment-history",
                          "Toggle payment history"
                        )}
                        onClick={() => setViewMore(!viewMore)}
                      >
                        {viewMore ? "View more" : "View less"}
                      </span>
                    </div>
                  )}
                </div>
              ) : isLoading ? (
                <div>
                  <Skeleton width={150} />
                </div>
              ) : (
                <div className="text-sm text-txtSecondary leading-[18.23px]">
                  No payments have been made
                </div>
              )}
            </div>
          </div>
        )}

        {invoiceData?.status?.toLowerCase() === "opened" ||
          (invoiceData?.status?.toLowerCase() === "open" &&
            showAccountDetails && (
              <div className="lg:px-10 px-5 py-2 w-full flex gap-5 justify-between items-center">
                <Button
                  text="Pay this invoice"
                  className="w-full"
                  {...testAttrs("pay-invoice", "Pay this invoice")}
                  onClick={() => {
                    navigate(`/invoice/${id}`);
                  }}
                  variant="variable"
                />
              </div>
            ))}
        {!isLoading && (
          <div className={`py-5 lg:px-10 px-5 bottom-0 flex justify-center `}>
            <QuestionsFooter isPreview={true} />
          </div>
        )}
      </div>
      <div
        className="absolute top-0 lg:w-[590px] h-full lg:right-[590px] right-0 z-10 w-full"
        style={{ display: isPaymentPreview ? "block" : "none" }}
        {...testAttrs("payment-receipt-preview")}
      >
        <PaymentPreview
          id={isPaymentPreviewId || ""}
          setId={setIsPaymentPreviewId}
          show={isPaymentPreview}
          setShow={setIsPaymentPreview}
          onDownloadCLick={(id: any, receiptId: any) =>
            handleDownloadPDF(id, receiptId)
          }
          isDownloading={isDownloading.toString()}
        />
      </div>
    </>
  );
};

export default InvoicePreview;
